import React from 'react';
import ReactBody from 'react-body';
import { StickyContainer } from 'react-sticky';
import { Element } from 'react-scroll';
import ActiveSection from '../../scripts/active-section';
import NavAngular from '../header/NavAngular';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Images
import DataBinding from '../../images/knowledge/data-binding.png';

const AngularConcepts = () => {
  var menuRef = React.createRef();

const componentsTS = 
`import { Component, Input } from '@angular/core';

@Component({
  selector: 'app-message-display',
  templateUrl: './message-display.component.html',
  styleUrls: ['./message-display.component.css']
})
export class MessageDisplayComponent {
  @Input() message: string = 'Default message';
}`;

const componentsHTML = 
`<div class="message-box">
  <p>{{ message }}</p>
</div>`;

const angularModuleTS = 
`import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MessageDisplayComponent } from './message-display.component';

@NgModule({
  declarations: [
    MessageDisplayComponent
  ],
  imports: [
    CommonModule
  ],
  exports: [
    MessageDisplayComponent
  ]
})
export class MessageDisplayModule { }`;

const customDirectiveCode = 
`import { Directive, ElementRef, HostListener, Input } from '@angular/core';

@Directive({
  selector: '[appHoverHighlight]'
})
export class HoverHighlightDirective {
  @Input() highlightColor: string = 'yellow';

  constructor(private el: ElementRef) {}

  @HostListener('mouseenter') onMouseEnter() {
    this.highlight(this.highlightColor);
  }

  @HostListener('mouseleave') onMouseLeave() {
    this.highlight('');
  }

  private highlight(color: string) {
    this.el.nativeElement.style.backgroundColor = color;
  }
}`;

const serviceCode = 
`@Injectable({ providedln: 'root' }) 
  export class MyService { 
    getData() {
    return ['Apple', 'Banana', 'Orange'];
  }
}`;

const serviceCodeUsage = 
`constructor(private mySenvice: MyService) {}`;

const observableCode = 
`import { Observable } from 'rxjs';

const observable = new Observable(observer => { 
  observer.next('Hello'); 
  observer.complete();
});

observable.subscribe(value => console.log(value))`;

const observableCodeTwo = 
`this.http.get('api/data').subscribe(data => console.log(data))`;

const fetchAllUsersService = 
`@Injectable({ providedIn: 'root' })
export class UserService {
  constructor(private http: HttpClient) {}

  getUsers(): Observable<User[]> {
    return this.http.get<User[]>('https://jsonplaceholder.typicode.com/users');
  }
}`;

const eventEmmiterOne = 
`@Output() notify = new EventEmitter<string>();

sendMessage() {
  this.notify.emit('Hello Parent!');
}`;

const eventEmmiterTwo = 
`<button (click)="sendMessage()">Send</button>
`;

const eventEmmiterThree = 
`<app-child (notify)="handleMessage($event)"></app-child>
`;

const routingOne = 
`const routes: Routes = [
  { path: 'home', component: HomeComponent },
  { path: 'about', component: AboutComponent },
];`;

const routingTwo = 
`<a routerLink="/home">Home</a>
<router-outlet></router-outlet>`;

const reactiveFormsOne = 
`this.form = new FormGroup({
  name: new FormControl('', Validators.required)
});`;

const reactiveFormsTwo = 
`<form [formGroup]="form">
  <input formControlName="name"> 
</form>
`;

const pipeOne = 
`{{ 1234.56 | currency }} <!-- $1,234.56 -->
{{ today | date:'shortDate' }} <!-- 2/17/25 -->
`;

const pipeTwo = 
`@Pipe({ name: 'capitalize' })
export class CapitalizePipe implements PipeTransform { 
  transform(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
}
`;

const lifeCycleHooks = 
`ngOnInit() { console.log('Component Initialized'); } 
ngOnChanges() { console.log('Input Property Changed'); } 
ngOnDestroy() { console.log('Component Destroyed'); }
`;

const lazyLoadingCode = 
`const routes: Routes = [
  { path: 'dashboard', loadChildren: () => import('./dashboard.module').then(m => m.DashboardModule) }
];
`;

const generateServiceCode = 
`import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private apiUrl = 'https://api.example.com/search'; // Replace with actual API

  constructor(private http: HttpClient) {}

  search(query: string): Observable<any[]> {
    return this.http.get<any[]>(\`\${this.apiUrl}?q=\${query}\`);
  }
}
`;

const UpdateTypeScriptCode = 
`import { Component } from '@angular/core'; 
import { SearchService } from '../search.service';

@Component({
  selector: 'app-search',
  templateUrl: ' . /search.component.html',
  styleUrls: ['./search.component.css'],
})

export class SearchComponent { 
  searchText = ''; 
  results: any[] = [];

  constructor(private searchService: SearchService) {}

    onSearch() {
      if (this.searchText) {
        this.searchService.search(this.searchText).subscribe((data) => { 
          this.results = data;
      });
    }
  }
}
`;

const UpdateTemplateCode = 
`<input type="text" [(ngModel)]="searchText" placeholder="Search..." /> 
  <button (click)="onSearch()">Search</button>

  <ul>
    <li *ngFor="let item of results">{{ item.name }}</li>
  </ul>
`;

const AddModuleCode = 
`import { HttpClientModule } from '@angular/common/http';

@NgModule({
  imports: [HttpClientModule],
})
`;

const pipeCode = 
`import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
  name: 'search'
})

export class SearchPipe implements PipeTransform { 
  transform(items: any[], searchText: string): any[] { 
    if (!items || !searchText) return items;
    return items.filter(item => item.name.toLowerCase().includes(searchText.toLowerCase()));
  }
}
`;

const addPipeCode = 
`<input type="text" [(ngModel)]="searchText" placeholder="Search..." />

<ul>
  <li *ngFor="let item of items | search: searchText">
    {{ item.name }
  </li>
</ul>
`;

const CreateDirectiveCode = 
`import { Directive, ElementRef, HostListener, Input } from '@angular/core';

@Directive({
  selector: '[appHighlight]'
})

export class HighlightDirective {
  @Input() highlightColor: string = 'yellow'; // Default color

  constructor(private el: ElementRef) {}

  @HostListener('mouseenter') onMouseEnter() {
    this.el.nativeElement.style.backgroundColor = this.highlightColor;
  }

  @HostListener('mouseleave') onMouseLeave() {
    this.el.nativeElement.style.backgroundColor = '';
  }
}
`;

const UseDirectiveCode = 
`<p appHighlight highlightColor="lightblue">Hover over me to highlight!</p> 
<p appHighlight>Default highlight color (yellow)</p>
`;

const declareDirectiveCode = 
`import { HighlightDirective } from './highlight.directive';

@NgModule({
  declarations : [HighlightDirective]
})
`;

const fetchAllUsersTs = 
`@Component({  
  selector: 'app-user-list', 
  templateUrl: './user-list.component.html' 
})
export class UserListComponent implements OnInit {
  users: User[] = [];

  constructor(private userService: UserService) {}

  ngOnInit() {
    this.userService.getUsers().subscribe(data => this.users = data);
  }
}`;

const fetchAllUsersHtml = 
`<h2>Users</h2>
<ul>
  <li *ngFor="let user of users">{{ user.name }}</li>
</ul>`;

const fetchAllSingleUser = 
`getUserById(id) {
  return this.http.get(\`https://jsonplaceholder.typicode.com/users/\${id}\`);
}`;

const fetchAllSingleUserTs = 
`@Component({ 
  selector: 'app-user-detail', 
  templateUrl: './user-detail.component.html' 
})
export class UserDetailComponent implements OnInit {
  user!: User;

  constructor(private userService: UserService) {}

  ngOnInit() {
    const userId = 1; // hardcoded for example
    this.userService.getUserById(userId).subscribe(data => this.user = data);
  }
}`;

const fetchAllSingleUserHtml = 
`<h3>User Detail</h3>
<p>Name: {{ user.name }}</p>
<p>Email: {{ user.email }}</p>`;

const postService = 
`addUser(newUser: User): Observable<User> {
  return this.http.post<User>('https://jsonplaceholder.typicode.com/users', newUser);
}`;

const postServiceTs = 
`newUser: User = { name: '', email: '' } as User;

addUser() {
  this.userService.addUser(this.newUser).subscribe(user => {
    this.users.push(user); // update UI
    this.newUser = { name: '', email: '' } as User; // reset form
  });
}`;

const postServiceHtml = 
`<form (ngSubmit)="addUser()">
  <input [(ngModel)]="newUser.name" name="name" placeholder="Name" required>
  <input [(ngModel)]="newUser.email" name="email" placeholder="Email" required>
  <button type="submit">Add User</button>
</form>`;

const angularModule = 
`<form (ngSubmit)="addUser()">
  <input [(ngModel)]="newUser.name" name="name" placeholder="Name" required>
  <input [(ngModel)]="newUser.email" name="email" placeholder="Email" required>
  <button type="submit">Add User</button>
</form>`;

  return (
    <section>
      <ReactBody className="knowledge-page" />
      <NavAngular ref={menuRef} />
      <section className='right-section flex-group flex-align-center'>
      <StickyContainer>
        <Element className='element'>
          <section id="section-1" className='content-section flex-group flex-center'>
            <ActiveSection index="1" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Components</h2>
                <h3>What</h3>
                <p>
                  Components are the building blocks of an Angular app. Each component 
                  consists of an HTML template, a TypeScript class, and styles.
                </p>
                <h3>Why</h3>
                <p>
                  They help create a structured, modular UI by encapsulating logic and UI elements.
                </p>
                <h3>How</h3>
                  <p>ng generate component my-component</p>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {componentsTS}
                </SyntaxHighlighter>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {componentsHTML}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-2" className='content-section flex-group flex-center'>
            <ActiveSection index="2" refToMenu={menuRef}>
              <section className='script text-align-left flex-group flex-center flex-column'>
                <h2>Modules (NgModule)</h2>
                <h3>What</h3>
                <p>
                  Modules organize an Angular app into cohesive blocks.
                </p>
                <h3>Why</h3>
                <p>
                  They enable code organization, lazy loading, and dependency injection.
                </p>
                <h3>How</h3>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {angularModuleTS}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-3" className='content-section flex-group flex-center'>
            <ActiveSection index="3" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Data Binding</h2>
                <h3>What?</h3>
                <p>Binding links the data in the TypeScript class to the template.</p>
                <h3>Why?</h3>
                <p>It keeps the UI in sync with the data model.</p>
                <h3>How?</h3>
                <img className="mw30" src={DataBinding} />
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-4" className='content-section flex-group flex-center'>
            <ActiveSection index="4" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Directives</h2>
                <h3>What</h3>
                <p>
                  Directives extend HTML by adding behavior to elements.
                </p>
                <h3>Why</h3>
                <p>
                  They enable DOM manipulation and reusable UI logic.
                </p>
                <h3>How</h3>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {customDirectiveCode}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-5" className='content-section flex-group flex-center'>
            <ActiveSection index="5" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Services & Dependency Injection (DI)</h2>
                <h3>What?</h3>
                <p>Services handle business logic and data fetching, shared across components.</p>
                <h3>Why?</h3>
                <p>To maintain separation of concerns and avoid redundant code.</p>
                <h3>How?</h3>
                <p>ng generate service myService</p>
                <strong>Service example:</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {serviceCode}
                </SyntaxHighlighter>
                <strong>Use in a component:</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {serviceCodeUsage}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-6" className='content-section flex-group flex-center'>
            <ActiveSection index="6" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Observables & RxJS</h2>
                <h3>What?</h3>
                <p>Observables handle asynchronous operations and event streams.</p>
                <h3>Why?</h3>
                <p>They provide better control over async data (compared to Promises) with operators like map, filter, merge.</p>
                <h3 className="margin-bottom-small">How?</h3>
                <strong>Basic Observable Example in Your Service.ts:</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {observableCode}
                </SyntaxHighlighter>
                <strong>HTTP Request with Observables in Your Component.ts</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {observableCodeTwo}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-7" className='content-section flex-group flex-center'>
            <ActiveSection index="7" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>EventEmitters</h2>
                <h3>What?</h3>
                <p>A way for a child component to communicate with a parent.</p>
                <h3>Why?</h3>
                <p>It allows components to be loosely coupled.</p>
                <h3 className="margin-bottom-small">How?</h3>
                <strong>Child Component:</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {eventEmmiterOne}
                </SyntaxHighlighter>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {eventEmmiterTwo}
                </SyntaxHighlighter>
                <strong>Parent Component:</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {eventEmmiterThree}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-8" className='content-section flex-group flex-center'>
            <ActiveSection index="8" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Routing</h2>
                <h3>What?</h3>
                <p>Routing allows navigation between different views in an Angular app.</p>
                <h3>Why?</h3>
                <p>It enables a single-page application experience.</p>
                <h3 className="margin-bottom-small">How?</h3>
                <strong>Define routes:</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {routingOne}
                </SyntaxHighlighter>
                <strong>Use in a template:</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {routingTwo}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-9" className='content-section flex-group flex-center'>
            <ActiveSection index="9" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Forms (Reactive & Template-Driven)</h2>
                <h3>What?</h3>
                <p>Angular provides two ways to handle forms:</p>
                <ul className="text-align-left">
                  <li>Template-Driven Forms (simpler, uses ngModel)</li>
                  <li>Reactive Forms (more scalable, uses FormGroup & FormControl)</li>
                </ul>
                <h3>Why?</h3>
                <p>Forms are essential for user input handling.</p>
                <h3 className="margin-bottom-small">How?</h3>
                <strong>Reactive Form Example:</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {reactiveFormsOne}
                </SyntaxHighlighter>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {reactiveFormsTwo}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-10" className='content-section flex-group flex-center'>
            <ActiveSection index="10" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Pipes</h2>
                <h3>What?</h3>
                <p>Pipes transform data before displaying it in a template.</p>
                <h3>Why?</h3>
                <p>They make data representation cleaner and more readable.</p>
                <h3 className="margin-bottom-small">How?</h3>
                <strong>Built-in Pipes:</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {pipeOne}
                </SyntaxHighlighter>
                <strong>Custom Pipe:</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {pipeTwo}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-11" className='content-section flex-group flex-center'>
            <ActiveSection index="11" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Lifecycle Hooks</h2>
                <h3>What?</h3>
                <p>Hooks let you tap into different stages of a component’s life.</p>
                <h3>Why?</h3>
                <p>They help with initialization, cleanup, and performance optimization.</p>
                <h3 className="margin-bottom-small">How?</h3>
                <strong>Common Hooks:</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {lifeCycleHooks}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-12" className='content-section flex-group flex-center'>
            <ActiveSection index="12" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Lazy Loading</h2>
                <h3>What?</h3>
                <p>Lazy loading loads modules only when needed.</p>
                <h3>Why?</h3>
                <p>It improves performance by reducing initial bundle size.</p>
                <h3 className="margin-bottom-small">How?</h3>
                <strong>Define a lazy-loaded module:</strong>
                <p>
                  This code implements lazy loading in Angular. Instead of loading all 
                  modules upfront, it loads <strong className="red">DashboardModule only when the user navigates 
                  to /dashboard.</strong> The import() statement dynamically imports the module, 
                  reducing the initial application bundle size and improving performance.
                </p>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {lazyLoadingCode}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-13" className='content-section flex-group flex-center'>
            <ActiveSection index="13" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Generate Service</h2>
                <h3 className='margin-top-0'> (ng generate service search)</h3>
                <strong>Handles the API call:</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {generateServiceCode}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-14" className='content-section flex-group flex-center'>
            <ActiveSection index="14" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Update Component TypeScript</h2>
                <h3 className='margin-top-0'> (ng generate component search)</h3>
                <strong>Handles user input and calls the service:</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {UpdateTypeScriptCode}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-15" className='content-section flex-group flex-center'>
            <ActiveSection index="15" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Update Component Template</h2>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {UpdateTemplateCode}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-16" className='content-section flex-group flex-center'>
            <ActiveSection index="16" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Add the HttpClientModule to app.module</h2>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {AddModuleCode}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-17" className='content-section flex-group flex-center'>
            <ActiveSection index="17" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Create the Pipe</h2>
                <strong>ng generate pipe search</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {pipeCode}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-18" className='content-section flex-group flex-center'>
            <ActiveSection index="18" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Add the Pipe to Search Template for our Search</h2>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {addPipeCode}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-19" className='content-section flex-group flex-center'>
            <ActiveSection index="19" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Create Directive</h2>
                <strong>ng generate directive highlight</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {CreateDirectiveCode}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-20" className='content-section flex-group flex-center'>
            <ActiveSection index="20" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Use the Directive in our Template</h2>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {UseDirectiveCode}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-21" className='content-section flex-group flex-center'>
            <ActiveSection index="21" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Declare Directive in our Module</h2>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {declareDirectiveCode}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-22" className='content-section flex-group flex-center'>
            <ActiveSection index="22" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Fetch all users service</h2>
                <strong>user.service.ts</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {fetchAllUsersService}
                </SyntaxHighlighter>
                <strong>user-list.component.ts</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {fetchAllUsersTs}
                </SyntaxHighlighter>
                <strong>user-list.component.html</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {fetchAllUsersHtml}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-23" className='content-section flex-group flex-center'>
            <ActiveSection index="23" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>Fetch a Single User by ID</h2>
                <strong>user.service.ts</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {fetchAllSingleUser}
                </SyntaxHighlighter>
                <strong>user-list.component.ts</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {fetchAllSingleUserTs}
                </SyntaxHighlighter>
                <strong>user-list.component.html</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {fetchAllSingleUserHtml}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-24" className='content-section flex-group flex-center'>
            <ActiveSection index="24" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2>POST Data (Create a New User)</h2>
                <strong>user.service.ts</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {postService}
                </SyntaxHighlighter>
                <strong>user-list.component.ts</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {postServiceTs}
                </SyntaxHighlighter>
                <strong>user-list.component.html</strong>
                <SyntaxHighlighter className="w-100" language="typescript" style={vscDarkPlus}>
                  {postServiceHtml}
                </SyntaxHighlighter>
              </section>
            </ActiveSection>
          </section>
        </Element>
        <Element className='element'>
          <section id="section-25" className='content-section flex-group flex-center'>
            <ActiveSection index="25" refToMenu={menuRef}>
              <section className='script flex-group flex-center flex-column'>
                <h2 className='padding-top-large'>Keywords</h2>
                <pre>
                  <code>
                  <ul className='align-left'>
                    <li><strong>@Component</strong>: Decorator to define an Angular component with metadata like selector, template, and style.</li>
                    <li><strong>@ContentChild</strong>: Allows accessing a single content child element (projection).</li>
                    <li><strong>@ContentChildren</strong>: Allows accessing multiple content child elements (projection).</li>
                    <li><strong>@Directive</strong>: Decorator for creating custom DOM behavior or structural changes.</li>
                    <li><strong>@EventEmitter</strong>: Class used to create custom events for components and directives.</li>
                    <li><strong>@HostBinding</strong>: Binds a property of the host element to a property of a directive or component.</li>
                    <li><strong>@HostListener</strong>: Decorator to listen to events on the host element in directives or components.</li>
                    <li><strong>@Injectable</strong>: Decorator to mark a class as available for dependency injection.</li>
                    <li><strong>@Input</strong>: Decorator to bind input properties of a component to its parent.</li>
                    <li><strong>@Output</strong>: Decorator to bind output properties of a component to emit events to the parent.</li>
                    <li><strong>ActivatedRoute</strong>: Provides access to route parameters, query parameters, and route data.</li>
                    <li><strong>AbstractControl</strong>: Base class for form controls, form groups, and form arrays.</li>
                    <li><strong>Angular Forms</strong>: Can be <strong>template-driven</strong> or <strong>reactive</strong> for handling forms and validation.</li>
                    <li><strong>Angular Universal</strong>: Server-side rendering (SSR) for Angular apps, improving performance and SEO.</li>
                    <li><strong>Binding</strong>: Mechanism to connect data between the component and template (property, event, or two-way).</li>
                    <li><strong>ChangeDetectorRef</strong>: Service for manually triggering change detection.</li>
                    <li><strong>Component</strong>: A core building block of Angular that controls a view (UI).</li>
                    <li><strong>Custom Pipe</strong>: A user-defined transformation function to format data for templates.</li>
                    <li><strong>Directive</strong>: Adds behavior or DOM modifications to elements (e.g., <code>*ngIf</code>, <code>*ngFor</code>, <code>ngModel</code>).</li>
                    <li><strong>Dependency Injection (DI)</strong>: Design pattern used to inject dependencies into components, services, etc.</li>
                    <li><strong>EventEmitter</strong>: Used with <strong>@Output</strong> to emit events from a child component to its parent.</li>
                    <li><strong>ErrorHandler</strong>: Global error handling service for Angular applications.</li>
                    <li><strong>FormsModule</strong>: Module for enabling <code>ngModel</code> and template-driven forms.</li>
                    <li><strong>HttpClient</strong>: Service for making HTTP requests and handling API calls.</li>
                    <li><strong>HttpClientModule</strong>: Imports the HttpClient service for making HTTP requests.</li>
                    <li><strong>Input</strong>: Property binding that allows a parent component to pass data to a child component.</li>
                    <li><strong>Interpolation double curly braces</strong>: Syntax to insert dynamic values into a template.</li>
                    <li><strong>Lazy Loading</strong>: Technique for loading Angular modules only when required, reducing initial load time.</li>
                    <li><strong>Life Cycle Hooks (e.g., <code>ngOnInit</code>, <code>ngOnDestroy</code>)</strong>: Methods that are called at different phases of a component or directive’s lifecycle.</li>
                    <li><strong>NgFor</strong>: Structural directive for looping over an array of data in a template.</li>
                    <li><strong>NgIf</strong>: Structural directive for conditionally including or excluding elements in the DOM.</li>
                    <li><strong>NgModule</strong>: A container for related components, services, pipes, and directives.</li>
                    <li><strong>NgModel</strong>: Directive for two-way data binding between a form input and a component property.</li>
                    <li><strong>Observable</strong>: Handles asynchronous operations and event streams, often used with RxJS.</li>
                    <li><strong>Output</strong>: Event binding for sending data from a child component to a parent component.</li>
                    <li><strong>Pipes</strong>: Functions to transform input data into a desired output format (e.g., <code>| date</code>, <code>| uppercase</code>).</li>
                    <li><strong>Platform</strong>: Represents the platform that Angular runs on (e.g., browser, mobile, etc.).</li>
                    <li><strong>ReactiveFormsModule</strong>: Module for building forms using reactive patterns with <code>FormGroup</code>, <code>FormControl</code>, etc.</li>
                    <li><strong>Renderer2</strong>: Service that provides a way to manipulate the DOM without directly accessing it, to ensure cross-platform compatibility.</li>
                    <li><strong>Route Guards</strong>: Services to control access to routes (e.g., <code>CanActivate</code>, <code>CanDeactivate</code>, <code>CanLoad</code>).</li>
                    <li><strong>Router</strong>: Service for managing application navigation and URL routing.</li>
                    <li><strong>RouterModule</strong>: Module that enables Angular routing by defining routes and adding them to your app.</li>
                    <li><strong>RouteParams</strong>: Parameters in the URL, accessible using <code>ActivatedRoute</code> to retrieve dynamic values.</li>
                    <li><strong>Service</strong>: Class that provides shared logic and data to multiple components via dependency injection.</li>
                    <li><strong>State Management</strong>: Techniques like <strong>NgRx</strong>, <strong>Akita</strong>, or <strong>Services</strong> for managing complex state in an Angular application.</li>
                    <li><strong>Subject</strong>: A type of Observable that allows for multicasting to multiple observers.</li>
                    <li><strong>Template</strong>: The HTML view part of a component, where dynamic content is rendered.</li>
                    <li><strong>Template-driven Forms</strong>: Forms built using the <code>FormsModule</code> where the template handles form control and validation.</li>
                    <li><strong>Two-way Binding</strong>: A combination of property binding and event binding that keeps the model and view in sync using <code>[(ngModel)]</code>.</li>
                    <li><strong>UpgradeModule</strong>: Used for upgrading AngularJS apps to Angular (used in hybrid applications).</li>
                    <li><strong>ViewChild</strong>: Decorator that allows accessing a single child element or directive within a template.</li>
                    <li><strong>ViewChildren</strong>: Decorator that allows accessing multiple child elements or directives within a template.</li>
                    <li><strong>Zone.js</strong>: Used by Angular to detect asynchronous operations and automatically trigger change detection.</li>    
                    <li><strong>Custom Validators</strong>: Create your own validation logic for form fields.</li>
                    <li><strong>ChangeDetectionStrategy</strong>: Defines how change detection is performed for a component (<code>Default</code> or <code>OnPush</code>).</li>
                    <li><strong>Decorator</strong>: Special functions that attach metadata to classes, properties, or methods.</li>
                    <li><strong>NgZone</strong>: Provides an execution context to allow for automatic triggering of change detection.</li>
                    <li><strong>TrackBy</strong>: A function used with <code>*ngFor</code> to optimize rendering when looping through lists, improving performance.</li>
                    <li><strong>ng-template</strong>: A container for template content that can be conditionally rendered (used with <code>*ngIf</code>, <code>*ngFor</code>, etc.).</li>
                    <li><strong>HttpInterceptor</strong>: Intercepts HTTP requests or responses globally for logging, error handling, or authentication.</li>
                    <li><strong>Service Workers</strong>: A browser feature for handling background tasks such as caching and offline functionality in Angular PWA (Progressive Web Apps).</li>
                    <li><strong>Web Workers</strong>: Background thread in web apps for offloading tasks to improve performance.</li>
                    <li><strong>SSR (Server-Side Rendering)</strong>: Rendering Angular applications on the server using <strong>Angular Universal</strong> to improve SEO and performance.</li>
                    <li><strong>Dynamic Component Loading</strong>: Loading components dynamically based on user interactions or conditions.</li> 
                    <li><strong>RxJS Operators</strong>: (e.g., <strong>map</strong>, <strong>switchMap</strong>, <strong>mergeMap</strong>, <strong>filter</strong>, <strong>debounceTime</strong>, etc.) Operators used with Observables to transform, filter, and handle asynchronous data.</li>
                  </ul>
                  </code>
                </pre>
              </section>
            </ActiveSection>
          </section>
        </Element>
      </StickyContainer>
      </section>
    </section>
  );
};

export default AngularConcepts;